import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { Controller, useWatch } from "react-hook-form";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

const InputField = ({
  name,
  control,
  label,
  type = "text",
  disabled = false,
  inputRef,
  defaultValue = "",
  value,
  onKeyDown,
  autoComplete,
  maxLength,
  showPasswordIcon = false,
  onChange,
  onClick,
  fontSize = "text-sm",
  inputSize = "h-8",
  leftIcon,
  rightIcon,
  iconBgColor = "bg-gray-100",
  inputBgColor = "bg-gray-50",
  onLeftIconClick,
  onRightIconClick,
  isFloatingLabel = true,
  placeholder = "",
  error,
  textTransform = "none", // 'none', 'uppercase', 'lowercase', 'capitalize', 'capitalizeFirst'
  suffix, // e.g. "mm/hg" for type="bp"
  showSearchIcon = false, // When true: magnifying glass inside input on right (clean search field style)
  ...rest
}) => {
  const { className: classNameProp, ...restProps } = rest;
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const internalInputRef = useRef(null);

  // BP: single field; store as text; no default placeholder (only when placeholder is explicitly passed)
  const isBp = type === "bp";
  const effectiveType = isBp ? "text" : type;
  const effectivePlaceholder = placeholder ?? "";
  const effectiveSuffix = isBp && suffix === undefined ? "mm/hg" : suffix;

  // Always call useWatch at top level, but only use it if control and name are provided
  const watchedValue = useWatch({ control, name, defaultValue });

  // Use watchedValue for controlled inputs, value for uncontrolled inputs
  const inputValue = control && name ? watchedValue : value;

  // Robust hasValue check for both controlled and uncontrolled cases
  const hasValue =
    inputValue !== null && inputValue !== undefined && inputValue !== "";

  const handleTogglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  const hasLeftIcon = !!leftIcon;
  const hasRightIcon = !!rightIcon && !showSearchIcon; // Don't use rightIcon box when showSearchIcon
  const hasPasswordIcon = type === "password" && showPasswordIcon;

  const getHeightClass = () => {
    switch (inputSize) {
      case "h-8":
        return "h-8";
      case "h-9":
        return "h-9";
      case "h-10":
        return "h-10";
      case "h-11":
        return "h-11";
      case "h-12":
        return "h-12";
      default:
        return "h-9";
    }
  };

  const getFontSizeClass = () => {
    switch (fontSize) {
      case "text-xs":
        return "text-xs";
      case "text-sm":
        return "text-sm";
      case "text-base":
        return "text-base";
      case "text-lg":
        return "text-lg";
      default:
        return "text-sm";
    }
  };

  // Transform text based on textTransform prop
  const transformText = (text) => {
    if (!text || typeof text !== "string") return text;

    switch (textTransform) {
      case "uppercase":
        return text.toUpperCase();

      case "lowercase":
        return text.toLowerCase();

      case "capitalize":
        // Capitalize first letter of each word
        return text
          .split(" ")
          .map((word) => {
            if (word.length === 0) return word;
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          })
          .join(" ");

      case "capitalizeFirst":
        // Capitalize only the first letter of the entire string
        return text.charAt(0).toUpperCase() + text.slice(1);

      case "none":
      default:
        return text;
    }
  };

  // Handle text transformation with cursor position preservation
  const handleTextTransformChange = (e, callback) => {
    if (textTransform === "none") {
      callback(e);
      return;
    }

    const input = e.target;
    const cursorPosition = input.selectionStart;
    const originalValue = e.target.value;
    const transformedValue = transformText(originalValue);

    // Create a modified event with transformed value
    const modifiedEvent = {
      ...e,
      target: {
        ...e.target,
        value: transformedValue,
      },
    };

    callback(modifiedEvent);

    // Restore cursor position after React updates
    setTimeout(() => {
      const inputElement = inputRef || internalInputRef.current;
      if (inputElement && document.activeElement === inputElement) {
        inputElement.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, 0);
  };

  // Render uncontrolled input if control or name is not provided
  if (!control || !name) {
    return (
      <div className="relative w-full">
        {/* Floating Label */}
        {label && (
          <label
            htmlFor={name}
            className={`${isFloatingLabel
                ? `absolute transition-all duration-200 ease-in-out pointer-events-none z-10 ${isFocused || hasValue
                  ? "top-0 text-xs scale-75 origin-top-left bg-white px-1"
                  : "top-1/2 text-sm scale-100 origin-center"
                }`
                : "block mb-1"
              } ${getFontSizeClass()} ${error
                ? "text-red-500"
                : isFocused || hasValue
                  ? "text-blue-600"
                  : "text-gray-500"
              }`}
            style={
              isFloatingLabel
                ? {
                  left: hasLeftIcon ? "52px" : "14px",
                  transform:
                    isFocused || hasValue
                      ? "translateY(-50%) scale(0.75)"
                      : "translateY(-50%) scale(1)",
                  transformOrigin: "left center",
                }
                : {}
            }
          >
            {label}
          </label>
        )}

        <div className="relative flex items-center gap-1 flex-wrap w-full">
          <div
            className={`relative flex items-stretch transition-all rounded-md flex-1 min-w-0`}
          >
            {/* Left Icon */}
            {hasLeftIcon && (
              <div
                className={`flex items-center justify-center w-10 ${getHeightClass()} ${iconBgColor} border border-r-0 ${error ? "border-red-500" : "border-gray-300"
                  } rounded-l-md ${onLeftIconClick ? "cursor-pointer hover:opacity-80" : ""
                  } ${disabled ? "opacity-50" : ""
                  } transition-colors flex-shrink-0`}
                style={{ minWidth: "2.5rem", width: "2.5rem" }}
                onClick={onLeftIconClick}
              >
                {React.cloneElement(leftIcon, {
                  className: `w-4 h-4 flex-shrink-0 ${leftIcon.props?.className || "text-gray-500"
                    }`,
                })}
              </div>
            )}

            {/* Input */}
            <div
              className={`flex items-center flex-1 min-w-0 ${getHeightClass()} ${getFontSizeClass()} border ${showSearchIcon ? "bg-white" : inputBgColor} ${error ? "border-red-500" : "border-gray-300"
                } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${hasLeftIcon ? "rounded-l-none border-l-0" : "rounded-l-md"
                } ${hasRightIcon || hasPasswordIcon
                  ? "rounded-r-none border-r-0"
                  : "rounded-r-md"
                } focus-within:outline-none focus-within:ring-0 focus-within:shadow-none ${error ? "focus-within:border-red-500" : "focus-within:border-gray-300"} transition-colors relative`}
            >
              <input
                type={
                  effectiveType === "password" && !isPasswordVisible
                    ? "password"
                    : effectiveType === "password"
                      ? "text"
                      : effectiveType
                }
                id={name}
                className={`w-full outline-none focus:outline-none focus:ring-0 focus:border-0 bg-transparent px-3 py-1 ${showSearchIcon ? "pr-9" : ""} ${getFontSizeClass()} ${hasValue || isFocused ? "text-gray-800" : "text-gray-400"
                  } ${classNameProp || ""}`}
                style={{ outline: "none" }}
                ref={inputRef || internalInputRef}
                value={inputValue ?? ""}
                autoComplete={autoComplete}
                maxLength={maxLength}
                onKeyDown={onKeyDown}
                placeholder={
                  !(isFocused || hasValue) && !(isFloatingLabel && label)
                    ? effectivePlaceholder
                    : ""
                }
                onFocus={(e) => {
                  setIsFocused(true);
                  if (restProps.onFocus) restProps.onFocus(e);
                }}
                onBlur={(e) => {
                  setIsFocused(false);
                  if (restProps.onBlur) restProps.onBlur(e);
                }}
                onChange={(e) => {
                  handleTextTransformChange(e, (modifiedEvent) => {
                    if (onChange) onChange(modifiedEvent);
                  });
                }}
                onClick={onClick}
                disabled={disabled}
                {...restProps}
              />

              {/* Right-side icons (like password eye) */}
              {hasPasswordIcon && (
                <button
                  type="button"
                  onClick={handleTogglePasswordVisibility}
                  className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-100 flex-shrink-0"
                  aria-label={
                    isPasswordVisible ? "Hide password" : "Show password"
                  }
                  disabled={disabled}
                >
                  {isPasswordVisible ? (
                    <EyeSlashIcon className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <EyeIcon className="w-4 h-4 flex-shrink-0" />
                  )}
                </button>
              )}
              {/* Search icon inside input (clean search field style) */}
              {showSearchIcon && (
                <MagnifyingGlassIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none flex-shrink-0" />
              )}
            </div>

            {/* Right Icon */}
            {hasRightIcon && (
              <div
                className={`flex items-center justify-center w-10 ${getHeightClass()} ${iconBgColor} border border-l-0 ${error ? "border-red-500" : "border-gray-300"
                  } rounded-r-md ${onRightIconClick ? "cursor-pointer hover:opacity-80" : ""
                  } ${disabled ? "opacity-50" : ""
                  } transition-colors flex-shrink-0`}
                style={{ minWidth: "2.5rem", width: "2.5rem" }}
                onClick={onRightIconClick}
              >
                {React.cloneElement(rightIcon, {
                  className: `w-4 h-4 flex-shrink-0 ${rightIcon.props?.className || "text-gray-500"
                    }`,
                })}
              </div>
            )}
          </div>
          {effectiveSuffix && (
            <span className={`${getFontSizeClass()} text-gray-500 shrink-0`}>{effectiveSuffix}</span>
          )}
        </div>

        {/* Error Message */}
        {error && <p className="mt-1 text-xs text-red-500">{error.message}</p>}
      </div>
    );
  }

  // Controlled input with react-hook-form
  return (
    <div className="relative w-full">
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field, fieldState }) => {
          const hasError = Boolean(fieldState.error || error);

          // Label floats when focused or when there is a value
          const shouldFloatLabel = isFloatingLabel && (isFocused || hasValue);

          return (
            <>
              {/* Floating Label */}
              {label && (
                <label
                  htmlFor={name}
                  className={`${isFloatingLabel
                      ? `absolute transition-all duration-200 ease-in-out pointer-events-none z-10 ${shouldFloatLabel
                        ? "top-0 text-xs scale-75 origin-top-left bg-white px-1"
                        : "top-1/2 text-sm scale-100 origin-center"
                      }`
                      : "block mb-1"
                    } ${getFontSizeClass()} ${hasError
                      ? "text-red-500"
                      : shouldFloatLabel
                        ? "text-blue-600"
                        : "text-gray-800"
                    }`}
                  style={
                    isFloatingLabel
                      ? {
                        left: hasLeftIcon ? "52px" : "14px",
                        transform: shouldFloatLabel
                          ? "translateY(-50%) scale(0.75)"
                          : "translateY(-50%) scale(1)",
                        transformOrigin: "left center",
                      }
                      : {}
                  }
                >
                  {label}
                </label>
              )}

              <div className="relative flex items-center gap-1 flex-wrap w-full">
                <div
                  className={`relative flex items-stretch transition-all rounded-md flex-1 min-w-0`}
                >
                  {/* Left Icon */}
                  {hasLeftIcon && (
                    <div
                      className={`flex items-center justify-center w-10 ${getHeightClass()} ${iconBgColor} border border-r-0 ${hasError ? "border-red-500" : "border-gray-300"
                        } rounded-l-md ${onLeftIconClick ? "cursor-pointer hover:opacity-80" : ""
                        } ${disabled ? "opacity-50" : ""
                        } transition-colors flex-shrink-0`}
                      style={{ minWidth: "2.5rem", width: "2.5rem" }}
                      onClick={onLeftIconClick}
                    >
                      {React.cloneElement(leftIcon, {
                        className: `w-4 h-4 flex-shrink-0 ${leftIcon.props?.className || "text-gray-500"
                          }`,
                      })}
                    </div>
                  )}

                  {/* Input */}
                  <div
                    className={`flex items-center flex-1 min-w-0 ${getHeightClass()} ${getFontSizeClass()} border ${showSearchIcon ? "bg-white" : inputBgColor} ${hasError ? "border-red-500" : "border-gray-300"
                      } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${hasLeftIcon ? "rounded-l-none border-l-0" : "rounded-l-md"
                      } ${hasRightIcon || hasPasswordIcon
                        ? "rounded-r-none border-r-0"
                        : "rounded-r-md"
                      } focus-within:outline-none focus-within:ring-0 focus-within:shadow-none ${hasError ? "focus-within:border-red-500" : "focus-within:border-gray-300"} transition-colors relative`}
                  >
                    <input
                      type={
                        effectiveType === "password" && !isPasswordVisible
                          ? "password"
                          : effectiveType === "password"
                            ? "text"
                            : effectiveType
                      }
                      id={name}
                      className={`w-full outline-none focus:outline-none focus:ring-0 focus:border-0 bg-transparent px-3 py-1 ${showSearchIcon ? "pr-9" : ""} ${getFontSizeClass()} ${hasValue || isFocused
                          ? "text-gray-800"
                          : "text-gray-400"
                        } ${classNameProp || ""}`}
                      style={{ outline: "none" }}
                      ref={inputRef || internalInputRef}
                      {...field}
                      value={inputValue ?? ""}
                      autoComplete={autoComplete}
                      maxLength={maxLength}
                      onKeyDown={onKeyDown}
                      placeholder={
                        !(isFocused || hasValue) && !(isFloatingLabel && label)
                          ? effectivePlaceholder
                          : ""
                      }
                      onFocus={(e) => {
                        setIsFocused(true);
                        if (restProps.onFocus) restProps.onFocus(e);
                      }}
                      onBlur={(e) => {
                        setIsFocused(false);
                        if (restProps.onBlur) restProps.onBlur(e);
                      }}
                      onChange={(e) => {
                        handleTextTransformChange(e, (modifiedEvent) => {
                          field.onChange(modifiedEvent);
                          if (onChange) onChange(modifiedEvent);
                        });
                      }}
                      onClick={onClick}
                      disabled={disabled}
                      {...restProps}
                    />

                    {/* Right-side icons (like password eye) */}
                    {hasPasswordIcon && (
                      <button
                        type="button"
                        onClick={handleTogglePasswordVisibility}
                        className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-100 flex-shrink-0"
                        aria-label={
                          isPasswordVisible ? "Hide password" : "Show password"
                        }
                        disabled={disabled}
                      >
                        {isPasswordVisible ? (
                          <EyeSlashIcon className="w-4 h-4 flex-shrink-0" />
                        ) : (
                          <EyeIcon className="w-4 h-4 flex-shrink-0" />
                        )}
                      </button>
                    )}
                    {/* Search icon inside input (clean search field style) */}
                    {showSearchIcon && (
                      <MagnifyingGlassIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none flex-shrink-0" />
                    )}
                  </div>

                  {/* Right Icon */}
                  {hasRightIcon && (
                    <div
                      className={`flex items-center justify-center w-10 ${getHeightClass()} ${iconBgColor} border border-l-0 ${hasError ? "border-red-500" : "border-gray-300"
                        } rounded-r-md ${onRightIconClick
                          ? "cursor-pointer hover:opacity-80"
                          : ""
                        } ${disabled ? "opacity-50" : ""
                        } transition-colors flex-shrink-0`}
                      style={{ minWidth: "2.5rem", width: "2.5rem" }}
                      onClick={onRightIconClick}
                    >
                      {React.cloneElement(rightIcon, {
                        className: `w-4 h-4 flex-shrink-0 ${rightIcon.props?.className || "text-gray-500"
                          }`,
                      })}
                    </div>
                  )}
                </div>
                {effectiveSuffix && (
                  <span className={`${getFontSizeClass()} text-gray-500 shrink-0`}>{effectiveSuffix}</span>
                )}
              </div>


            </>
          );
        }}
      />
    </div>
  );
};

InputField.propTypes = {
  name: PropTypes.string,
  control: PropTypes.object,
  label: PropTypes.string,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  inputRef: PropTypes.object,
  defaultValue: PropTypes.string,
  value: PropTypes.any,
  onKeyDown: PropTypes.func,
  autoComplete: PropTypes.string,
  maxLength: PropTypes.number,
  showPasswordIcon: PropTypes.bool,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  fontSize: PropTypes.string,
  inputSize: PropTypes.string,
  leftIcon: PropTypes.element,
  rightIcon: PropTypes.element,
  iconBgColor: PropTypes.string,
  inputBgColor: PropTypes.string,
  onLeftIconClick: PropTypes.func,
  onRightIconClick: PropTypes.func,
  isFloatingLabel: PropTypes.bool,
  placeholder: PropTypes.string,
  error: PropTypes.object,
  textTransform: PropTypes.oneOf([
    "none",
    "uppercase",
    "lowercase",
    "capitalize",
    "capitalizeFirst",
  ]),
  suffix: PropTypes.string,
  showSearchIcon: PropTypes.bool,
};

export default InputField;
